import React, {useId, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CaretDown} from '../../icons';
import {
    buildDataSummary,
    durationLabel,
    TURN_KIND,
    turnHeadline,
    whenLabel,
} from '../../portal_usage/portalUsageState';
import {PortalTurnSteps} from './PortalTurnSteps';

/**
 * Un turno: una cosa que el cliente quiso saber, con los pasos del asistente plegados detrás.
 *
 * Es una FILA con regla horizontal, no una tarjeta. Con 8-25 turnos, 25 cajas con borde y radio
 * devuelven la lectura «cada interacción es un evento» que la agrupación existía para evitar; una
 * lista con reglas se lee como un log continuo. Lo único que queda del borde es la franja de color
 * de la izquierda, que distingue los tres tipos sin gastar una pastilla.
 *
 * Dos decisiones de accesibilidad que NO son cosméticas:
 *
 * 1. **El texto de la pregunta queda FUERA de todo control.** Antes la cabecera entera era el
 *    `<button>`, así que un lector de pantalla leía una pregunta de 90 caracteres como *nombre* del
 *    botón y el texto no se podía seleccionar ni copiar. Ahora el disclosure es sólo la línea de
 *    pasos.
 * 2. **Ningún turno arranca abierto.** Todo el rediseño consiste en que la línea de arriba ya diga
 *    lo que hay que saber; auto-abrir destacaría los *pasos de contexto*, que son el contenido
 *    menos interesante de la pantalla. De paso desaparece la clase entera de bugs «¿es el default o
 *    lo tocó el usuario?».
 */
export function PortalUsageTurn({turn, textVisible}) {
    const {t, i18n} = useTranslation();
    const [open, setOpen] = useState(false);
    const panelId = useId();

    const headline = turnHeadline(turn, {textVisible});
    const steps = turn.steps || [];
    // ⚠️ Con `truncated`, el backend anula `steps_count` A PROPÓSITO: los pasos previos de este
    // turno quedaron del otro lado del corte de la muestra, así que `steps.length` es un contador
    // incompleto. Caer a él deshace el trabajo del backend de no mentir.
    const stepsCount = typeof turn.steps_count === 'number'
        ? turn.steps_count
        : (turn.truncated ? null : steps.length);
    const when = whenLabel(turn.at, t);
    const duration = durationLabel(turn.duration_ms, t, i18n.language);
    const stepsTitle = t('portal_usage.steps_panel_title', 'Lo que hizo el asistente para responder');

    return (
        <li className={`pu-turn -${turn.kind ? turn.kind.toLowerCase() : 'data'}`}>
            <div className="pu-turn__meta">
                <span className="pu-turn__who">
                    {turn.user_name || t('portal_usage.actor_unknown_user', 'Usuario del portal')}
                </span>
                {when ? <><span className="pu-turn__sep">·</span><span>{when}</span></> : null}
                {/* El error REEMPLAZA a la duración: si falló, cuánto tardó es ruido. */}
                {turn.failed ? (
                    <>
                        <span className="pu-turn__sep">·</span>
                        <span className="pu-turn__err">
                            {t('portal_usage.question_failed', 'no devolvió datos')}
                        </span>
                    </>
                ) : duration ? (
                    <><span className="pu-turn__sep">·</span><span>{duration}</span></>
                ) : null}
                {turn.kind === TURN_KIND.DATA ? (
                    <span className="pu-kind -data">{t('portal_usage.turn_kind_data', 'Consulta')}</span>
                ) : null}
            </div>

            <Headline headline={headline} t={t} language={i18n.language}/>

            {/* Cero pasos → no se renderiza el control. Es una excepción consciente a la regla de
                que un control no se esconde con contador 0: esa regla protege FILTROS, donde
                aparecer y desaparecer se lee como un estado. Acá no hay nada que abrir, y un
                «ver (0)» deshabilitado es la promesa de nada. */}
            {stepsCount || (turn.truncated && steps.length) ? (
                <div className="pu-turn__more">
                    <button
                        type="button"
                        className="pu-turn__toggle"
                        aria-expanded={open}
                        aria-controls={panelId}
                        onClick={() => setOpen(value => !value)}
                    >
                        <span>
                            {stepsCount === null
                                ? t('portal_usage.turn_steps_truncated', 'pasos de contexto (muestra recortada)')
                                : (stepsCount === 1
                                    ? t('portal_usage.turn_steps_one', '1 paso de contexto')
                                    : t('portal_usage.turn_steps_many', '{{n}} pasos de contexto',
                                        {n: stepsCount}))}
                        </span>
                        <CaretDown className={`pu-turn__caret${open ? ' -open' : ''}`} size={12} weight="bold"/>
                    </button>
                </div>
            ) : null}

            {open ? <PortalTurnSteps steps={steps} panelId={panelId} title={stepsTitle}/> : null}
        </li>
    );
}

/**
 * Qué se lee como encabezado del turno.
 *
 * Las cuatro variantes NUNCA comparten string. Antes «no hay texto» y «no te lo mostramos» caían en
 * la misma leyenda («Consulta sin texto disponible»), y esa ambigüedad es el defecto que reportó
 * Eli: la agencia no podía saber si el cliente no escribió nada o si se lo estábamos ocultando.
 */
function Headline({headline, t, language}) {
    if (headline.kind === 'TEXT') {
        // El texto lo escribe un tercero: va como children de React, nunca interpolado en una key
        // ni por `dangerouslySetInnerHTML`.
        return <p className="pu-turn__q">{`«${headline.text}»`}</p>;
    }
    if (headline.kind === 'SUMMARY') {
        return <p className="pu-turn__q -data">{buildDataSummary(headline.summary, t, language)}</p>;
    }
    if (headline.kind === 'CONTEXT') {
        return (
            <p className="pu-turn__q -context">
                {t('portal_usage.turn_context_only', 'Sólo pasos de contexto, sin una consulta')}
            </p>
        );
    }
    if (headline.kind === 'HIDDEN') {
        return (
            <p className="pu-turn__q -hidden">
                {t('portal_usage.turn_text_hidden', 'Le preguntó algo al asistente')}
            </p>
        );
    }
    return (
        <p className="pu-turn__q -hidden">
            {t('portal_usage.turn_text_missing', 'Sin texto guardado para esta consulta')}
        </p>
    );
}

export default PortalUsageTurn;
